import { Box, Stack, Theme, Typography } from '@mui/material';
import {
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
} from 'react';

interface OtpCodeInputProps {
  value: string;
  onChange: (code: string) => void;
  length?: number;
  error?: string | null;
  disabled?: boolean;
  autoFocus?: boolean;
}

/**
 * Segmented one-time-code input: a row of single-digit cells with auto-advance,
 * backspace-to-previous, arrow navigation, paste, and OS SMS autofill support.
 * Controlled via a single `value` string so it drops straight into a form.
 */
export const OtpCodeInput: React.FC<OtpCodeInputProps> = ({
  value,
  onChange,
  length = 6,
  error,
  disabled,
  autoFocus,
}) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const focusAt = (i: number) => {
    const idx = Math.max(0, Math.min(length - 1, i));
    const el = refs.current[idx];
    el?.focus();
    el?.select();
  };

  const writeFrom = (start: number, chars: string) => {
    const next = digits.slice();
    let idx = start;
    for (const c of chars) {
      if (idx >= length) break;
      next[idx] = c;
      idx += 1;
    }
    onChange(next.join('').slice(0, length));
    focusAt(idx);
  };

  const handleInput = (i: number, raw: string) => {
    const clean = raw.replace(/\D/g, '');
    if (!clean) {
      const next = digits.slice();
      next[i] = '';
      onChange(next.join(''));
      return;
    }
    writeFrom(i, clean);
  };

  const handleKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = digits.slice();
      if (digits[i]) {
        next[i] = '';
        onChange(next.join(''));
      } else if (i > 0) {
        next[i - 1] = '';
        onChange(next.join(''));
        focusAt(i - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusAt(i + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, length);
    if (pasted) writeFrom(0, pasted);
  };

  return (
    <Stack alignItems="center" gap={1} sx={{ position: 'relative' }}>
      <Stack direction="row" gap={{ xs: 1, sm: 1.25 }} justifyContent="center">
        {digits.map((digit, i) => {
          const filled = digit !== '';
          return (
            <Box
              key={i}
              component="input"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              aria-label={`Digit ${i + 1}`}
              ref={(el: HTMLInputElement | null) => (refs.current[i] = el)}
              value={digit}
              disabled={disabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInput(i, e.target.value)
              }
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                handleKeyDown(i, e)
              }
              onPaste={handlePaste}
              onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                e.target.select()
              }
              sx={(theme: Theme) => ({
                width: { xs: 44, sm: 52 },
                height: { xs: 54, sm: 60 },
                textAlign: 'center',
                fontSize: { xs: 22, sm: 24 },
                fontWeight: 600,
                color: theme.palette.common.white,
                caretColor: '#22c55e',
                background: filled
                  ? 'rgba(34, 197, 94, 0.10)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: '1.5px solid',
                borderColor: error
                  ? theme.palette.error.main
                  : filled
                    ? 'rgba(34, 197, 94, 0.55)'
                    : 'rgba(255, 255, 255, 0.18)',
                borderRadius: '14px',
                outline: 'none',
                transition:
                  'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
                '&:focus': {
                  borderColor: error ? theme.palette.error.main : '#22c55e',
                  boxShadow: error
                    ? '0 0 0 4px rgba(235, 95, 95, 0.18)'
                    : '0 0 0 4px rgba(34, 197, 94, 0.18)',
                },
                '&:disabled': { opacity: 0.5 },
                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              })}
            />
          );
        })}
      </Stack>
      {error ? (
        <Typography variant="body2" color="error" sx={{ fontSize: 13 }}>
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
};
